<template>
    <div class="phone-field">
        <div ref="rootRef" class="phone-fields">
            <PrimeSelect
                :modelValue="selectedCountry"
                :options="filteredCountryOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Country Code"
                aria-label="Country code"
                class="phone-cc !rounded"
                :panelStyle="{ width: panelWidth ? `${panelWidth}px` : 'auto', marginTop: '4px' }"
                :pt="{ header: { style: { paddingTop: '4px' } } }"
                filter
                filterPlaceholder="Search countries…"
                @filter="onCountryFilter"
                @show="onShow"
                @hide="onHide"
                @update:modelValue="onCountrySelected"
            >
                <template #value="slotProps">
                    <div v-if="slotProps.value && slotProps.value.code" class="flex items-center">
                        <span>{{ slotProps.value.dial_code }}</span>
                    </div>
                    <span v-else>{{ slotProps.placeholder }}</span>
                </template>
                <template #option="slotProps">
                    <div class="flex items-center">
                        <img
                            :src="`/flags/${slotProps.option.code}.svg`"
                            class="mr-2 w-4 h-4"
                            :alt="slotProps.option.name"
                            loading="lazy"
                            @error="hideBrokenFlag"
                        />
                        <span>{{ slotProps.option.label }}</span>
                    </div>
                </template>
            </PrimeSelect>

            <PrimeInputText
                :id="inputId"
                :modelValue="displayNumber"
                @update:modelValue="onPhoneInput"
                @keydown="phoneKeydown"
                placeholder="555-555-5555"
                aria-label="Phone number"
                :aria-describedby="describedBy"
                :aria-invalid="Boolean(errorMessage) || undefined"
                class="phone-num"
                :invalid="Boolean(errorMessage)"
            />
        </div>

        <p :id="errorId" class="phone-error" :class="{ 'sr-only': !errorMessage }" role="alert">{{ errorMessage }}</p>
    </div>
</template>

<script setup lang="ts">
import countries from '@/assets/json/countries.json';
import { isValidPhone } from '@privacy-bee/validate/phone';

export interface PhoneValue {
    country_code: string;
    phone_number: string;
}

const props = defineProps<{
    modelValue: Partial<PhoneValue>;
    /** Additional help describing the number; this control owns its validation message. */
    ariaDescribedby?: string;
    /** An id for the number input, so a host form can point a label at it or move focus to it. */
    inputId?: string;
    /** A server rejection, presented in the same error region as this control's own rule. */
    error?: string;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: PhoneValue): void;
    /**
     * Whether the field holds a dialable number. This control owns the rule —
     * `@privacy-bee/validate`'s `isValidPhone` — so a host form gates its submit on what the
     * field reports rather than on a second copy of the same test that can drift
     * from the one deciding whether the field reads as invalid.
     */
    (e: 'update:valid', valid: boolean): void;
}>();

// Same country list / shape as AddPhoneModal — every entry carries `code`,
// `dial_code`, `name`, plus a label that combines name + dial code.
const countryOptions = countries.map((c) => ({
    label: `${c.name} (${c.dial_code})`,
    value: c,
    code: c.code.toLowerCase(),
    name: c.name,
    dial_code: c.dial_code,
}));

const model = computed<PhoneValue>(() => ({
    country_code: props.modelValue.country_code ?? '1',
    phone_number: props.modelValue.phone_number ?? '',
}));

const selectedCountry = computed(() => {
    const dial = model.value.country_code?.replace(/^\+/, '') ?? '';
    return countryOptions.find((c) => c.dial_code.replace(/^\+/, '') === dial)?.value
        ?? countryOptions.find((c) => c.code === 'us')?.value;
});

const rootRef = ref<HTMLElement | null>(null);
const panelWidth = ref(0);
const searchQuery = ref('');

const filteredCountryOptions = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return countryOptions;
    return countryOptions
        .filter((c) =>
            c.name.toLowerCase().includes(q)
            || c.dial_code.toLowerCase().includes(q)
            || c.label.toLowerCase().includes(q)
        )
        .sort((a, b) => {
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();
            const aDial = a.dial_code.toLowerCase();
            const bDial = b.dial_code.toLowerCase();
            if (aName === q && bName !== q) return -1;
            if (bName === q && aName !== q) return 1;
            if (aDial === q && bDial !== q) return -1;
            if (bDial === q && aDial !== q) return 1;
            if (aName.startsWith(q) && !bName.startsWith(q)) return -1;
            if (bName.startsWith(q) && !aName.startsWith(q)) return 1;
            return aName.localeCompare(bName);
        });
});

function onCountryFilter(event: { value: string }) {
    searchQuery.value = event.value || '';
}

function onShow() {
    searchQuery.value = '';
    if (rootRef.value) panelWidth.value = rootRef.value.offsetWidth;
}

function onHide() {
    searchQuery.value = '';
}

function onCountrySelected(countryObj: (typeof countries)[number] | null) {
    const dial = countryObj?.dial_code?.replace(/^\+/, '') ?? '1';
    emit('update:modelValue', { ...model.value, country_code: dial });
}

function hideBrokenFlag(e: Event) {
    (e.target as HTMLImageElement).style.display = 'none';
}

function formatPhoneNumber(value: string): string {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}-${digits.slice(10)}`;
}

function onPhoneInput(value?: string) {
    const digits = (value ?? '').replace(/\D/g, '');
    emit('update:modelValue', { ...model.value, phone_number: formatPhoneNumber(digits) });
}

function phoneKeydown(e: KeyboardEvent) {
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if ((e.ctrlKey || e.metaKey) && /^[acvxACVX]$/.test(e.key)) return;
    if (allowed.includes(e.key)) return;
    if (!/^[0-9]$/.test(e.key)) e.preventDefault();
}

// A stored number arrives as bare digits; the reader sees the same grouping they
// would get by typing it, because one formatter prints both.
const displayNumber = computed(() => formatPhoneNumber(model.value.phone_number));

const hasInput = computed(() => /\p{Nd}/u.test(model.value.phone_number));
const isValid = computed(() => isValidPhone(model.value.phone_number));
// An empty optional field reads as untouched; a partial number is invalid.
const isInvalid = computed(() => hasInput.value && !isValid.value);
const errorId = `phone-error-${useId()}`;
const errorMessage = computed(() => isInvalid.value ? 'Please enter a valid phone number.' : props.error);
const describedBy = computed(() => [props.ariaDescribedby, errorId].filter(Boolean).join(' '));

watch(isValid, (valid) => emit('update:valid', valid), { immediate: true });
</script>

<style scoped lang="scss">
.phone-field {
    @apply w-full;
}

.phone-error {
    @apply mt-1 text-sm font-normal leading-5;
    color: var(--danger-ink);
}

.phone-fields {
    @apply flex flex-row gap-2.5 w-full;

    .phone-cc {
        @apply w-32;
    }

    .phone-num {
        @apply flex-1 min-w-0;
    }
}
</style>
